import { useRef, useState } from "react";

import { Form, Formik } from "formik";
import { Alert, Form as UiForm } from "react-bootstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { catalogFormInitialValues, catalogFormValidationSchema } from "./catalog-form.form";
import { useCurrentlyProcessedPageData, useSendCatalogData } from "./catalog-form.api";
import { CatalogFormValues } from "./catalog-form.type";

import styles from "./catalog-form.module.scss";
import { Button, FormGroup, Select } from "../../components";
import { CatalogDataResponseItem, CatalogOutputRow } from "../../types";
import { Producer } from "../../types";
import {
  getCatalogExcelSheetData,
  getParsedRowData,
  isExcelFile,
  isXlsxFile,
  parseExcelCatalog,
} from "../../utils";
import {
  CATALOG_MODE_CONFIG,
  catalogVariantDisplayTextMap,
  producerDisplayTextMap,
} from "../../consts";
import { getVariantRawRowsData } from "../../utils/get-variant-raw-rows-data.util";

export const CatalogForm = () => {
  const catalogFileInputRef = useRef<HTMLInputElement | null>(null);
  const { mutate: sendCatalogData, status } = useSendCatalogData();
  const { data: processedPageStatus } = useCurrentlyProcessedPageData(status === "pending");
  const [parsedData, setParsedData] = useState<CatalogDataResponseItem[]>([]);
  const [excelExportRows, setExcelExportRows] = useState<CatalogOutputRow[]>([]);
  const [excelStatus, setExcelStatus] = useState<"idle" | "processing" | "success" | "error">(
    "idle",
  );
  const [excelError, setExcelError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const resetProcessingState = () => {
    setExcelExportRows([]);
    setExcelStatus("idle");
    setExcelError(null);
    setParsedData([]);
  };

  const handleFileDownload = (
    catalogData: CatalogDataResponseItem[],
    options: Parameters<typeof getParsedRowData>[1],
    variant: string,
  ) => {
    const rawData = getVariantRawRowsData(catalogData, variant);
    const parsedRows = rawData.map((dataItem) => getParsedRowData(dataItem, options));

    exportRowsToExcel(parsedRows);
  };

  const exportRowsToExcel = (rows: Record<string, unknown>[]) => {
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = { Sheets: { Magazynowe: worksheet }, SheetNames: ["Magazynowe"] };
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const fileData = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(fileData, "export.xlsx");
  };

  const exportCatalogRowsToExcel = async (rows: CatalogOutputRow[]) => {
    setIsExporting(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 0));

      const worksheet = XLSX.utils.aoa_to_sheet(getCatalogExcelSheetData(rows));
      const workbook = { Sheets: { Magazynowe: worksheet }, SheetNames: ["Magazynowe"] };
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const fileData = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      saveAs(fileData, "export.xlsx");
    } finally {
      setIsExporting(false);
    }
  };

  const processExcelCatalog = async (file: File) => {
    setExcelStatus("processing");
    setExcelError(null);

    try {
      const buffer = await file.arrayBuffer();
      const outputRows = parseExcelCatalog(buffer);

      setExcelExportRows(outputRows);
      setExcelStatus("success");
    } catch (error) {
      setExcelExportRows([]);
      setExcelStatus("error");
      setExcelError(
        error instanceof Error ? error.message : "Nie udało się przetworzyć pliku Excel.",
      );
    }
  };

  return (
    <Formik
      initialValues={catalogFormInitialValues}
      validationSchema={catalogFormValidationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={(values) => {
        if (values.catalogInputType === "excel") {
          if (values.catalogFile) {
            processExcelCatalog(values.catalogFile);
          }

          return;
        }

        sendCatalogData(values as unknown as CatalogFormValues, {
          onSettled: (data) => {
            setParsedData(data || []);
          },
        });
      }}
    >
      {({ setFieldValue, handleChange, values, errors }) => {
        const isPdfInput = values.catalogInputType === "pdf";
        const isExcelInput = values.catalogInputType === "excel";
        const isShamirProducer = values.producerName === Producer.SHAMIR;
        const catalogFileAccept = isShamirProducer
          ? ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf,.pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,.xlsx,.xls";

        return (
          <Form className={styles.catalogForm}>
            <div className={styles.catalogFormWrapperRow}>
              <FormGroup inputId="catalogFile" labelText="Plik katalogu">
                <UiForm.Control
                  ref={catalogFileInputRef}
                  name="catalogFile"
                  id="catalogFile"
                  type="file"
                  accept={catalogFileAccept}
                  isInvalid={"catalogFile" in errors}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const file = event.target.files?.[0];

                    setFieldValue("catalogFile", file);
                    setFieldValue(
                      "catalogInputType",
                      file ? (isExcelFile(file) ? "excel" : "pdf") : null,
                    );
                    resetProcessingState();
                  }}
                />
              </FormGroup>
            </div>
            {isPdfInput && (
              <div className={styles.catalogFormWrapperRow}>
                <FormGroup inputId="startPageIndex" labelText="Początkowa strona katalogu">
                  <UiForm.Control
                    name="startPageIndex"
                    id="startPageIndex"
                    type="number"
                    isInvalid={"startPageIndex" in errors}
                    onChange={handleChange}
                  />
                </FormGroup>
                <FormGroup inputId="endPageIndex" labelText="Końcowa strona katalogu">
                  <UiForm.Control
                    name="endPageIndex"
                    id="endPageIndex"
                    type="number"
                    isInvalid={"endPageIndex" in errors}
                    onChange={handleChange}
                  />
                </FormGroup>
              </div>
            )}
            <div className={styles.catalogFormWrapperRow}>
              <FormGroup inputId="producerName" labelText="Producent">
                <Select
                  name="producerName"
                  options={CATALOG_MODE_CONFIG.map(({ producer }) => ({
                    value: producer,
                    label: producerDisplayTextMap[producer],
                  }))}
                  isInvalid={"producerName" in errors}
                  onChange={(event) => {
                    const nextProducer = event.target.value;

                    if (
                      nextProducer === Producer.SHAMIR &&
                      values.catalogFile &&
                      !isXlsxFile(values.catalogFile)
                    ) {
                      setFieldValue("catalogFile", null);
                      setFieldValue("catalogInputType", null);

                      if (catalogFileInputRef.current) {
                        catalogFileInputRef.current.value = "";
                      }
                    }

                    setFieldValue("catalogVariant", null);
                    resetProcessingState();
                  }}
                />
              </FormGroup>
              <FormGroup inputId="catalogVariant" labelText="Wariant">
                <Select
                  name="catalogVariant"
                  isDisabled={!values.producerName}
                  options={
                    !!values.producerName
                      ? CATALOG_MODE_CONFIG.find(
                          (configItem) => configItem.producer === values.producerName,
                        )?.variants.map((variant) => ({
                          value: variant,
                          label: catalogVariantDisplayTextMap[variant],
                        })) || []
                      : []
                  }
                />
              </FormGroup>
            </div>
            <div className={styles.catalogFormWrapperRow}>
              <FormGroup inputId="vatPercent" labelText="Stawka % VAT">
                <UiForm.Control
                  name="vatPercent"
                  id="vatPercent"
                  type="number"
                  value={values.vatPercent}
                  isInvalid={"vatPercent" in errors}
                  onChange={handleChange}
                />
              </FormGroup>
              <FormGroup inputId="purpose" labelText="Przeznaczenie">
                <UiForm.Control
                  name="purpose"
                  id="purpose"
                  type="text"
                  value={values.purpose}
                  isInvalid={"purpose" in errors}
                  onChange={handleChange}
                />
              </FormGroup>
              <FormGroup inputId="merchandiseType" labelText="Typ towaru">
                <UiForm.Control
                  name="merchandiseType"
                  id="merchandiseType"
                  type="text"
                  value={values.merchandiseType}
                  isInvalid={"merchandiseType" in errors}
                  onChange={handleChange}
                />
              </FormGroup>
            </div>
            <div className={styles.alertWrapper}>
              {isPdfInput && status === "pending" && (
                <Alert variant="info">
                  Aktualnie przetwarzana jest strona numer:{" "}
                  {processedPageStatus?.processedPageNumber || values?.startPageIndex || 0}
                </Alert>
              )}
              {isExcelInput && excelStatus === "success" && (
                <Alert variant="success">
                  Przetworzono {excelExportRows.length} wierszy. Możesz pobrać plik wynikowy.
                </Alert>
              )}
              {isExcelInput && excelStatus === "error" && excelError && (
                <Alert variant="danger">{excelError}</Alert>
              )}
            </div>
            <div className={styles.catalogFormWrapperRow}>
              <Button
                type="submit"
                isProcessing={status === "pending" || excelStatus === "processing"}
              >
                Wyślij
              </Button>
              {parsedData?.length > 0 && status === "success" && isPdfInput && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    handleFileDownload(
                      parsedData,
                      {
                        vatPercent: values.vatPercent,
                        producer: values.producerName!,
                        purpose: values.purpose,
                        merchandiseType: values.merchandiseType,
                      },
                      `${values.producerName}-${values.catalogVariant}`,
                    );
                  }}
                >
                  Pobierz
                </Button>
              )}
              {excelExportRows.length > 0 && excelStatus === "success" && isExcelInput && (
                <Button
                  variant="secondary"
                  isProcessing={isExporting}
                  disabled={isExporting}
                  onClick={() => exportCatalogRowsToExcel(excelExportRows)}
                >
                  Pobierz
                </Button>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
